import React, { useEffect, useState } from 'react';
import Form from 'components/Form';
import { Column, Row } from 'components/Layout/Grid';
import Text from 'components/Text';
import important from 'img/icon-important.svg';
import TextArea from 'components/TextArea';
import { NoteCorrectionProps } from '../interface';
import { NewCorrectionProps, NoteCorrectionFormProps } from './interface';
import { Button } from 'components/Button';
import { Alert } from 'components/Alert';
import TicketServices from 'services/ticket.service';
import { useEnvironment } from 'hooks/useEnvironment';
import { EnvironmentTypes } from 'pages/OpenProtocol/Description/interface';
import { RemoveCharsAccents } from 'utils/RemoveCharsAccents';

const NoteCorrectionForm: React.FC<NoteCorrectionFormProps> = (props) => {
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const development = useEnvironment();
  const { order, setProtocolNumber, setRequestSuccess } = props;
  const MIN_DESCRIPTION_LENGHT = 15;
  const MAX_DESCRIPTION_LENGHT = 10000;

  useEffect(() => {
    const environment = process.env.REACT_APP_HOME_URL;

    if (development || environment === EnvironmentTypes.dev || environment === EnvironmentTypes.qas) {
      setDescription('CARTA DE CORRECAO SOLICITADA PELA EQUIPE DE DESENVOLVIMENTO CCARE AP');
    }
  }, []);

  const _ticketService = new TicketServices();

  const onSubmit = async (data: NoteCorrectionProps) => {
    setLoading(true);
    if (order?.id) {
      const newCorrection: NewCorrectionProps | boolean = await _ticketService.correctionLetter(order?.id, data.description, setError);
      setLoading(false);

      if (newCorrection) {
        setRequestSuccess(true);
        setProtocolNumber(newCorrection.data.data.protocolNumber);
      }
    }
  };

  const newDescription = (e: any) => {
    let target = e.target;
    let position = target.selectionStart;

    const newValue = RemoveCharsAccents(e);

    target.setSelectionRange(position, position);
    setDescription(newValue.value);
  };

  return (
    <Form
      id="new_request_correction_form"
      onValidate={(data: NoteCorrectionProps) => onSubmit(data)}
    >
      <Column
        className="bg-white background-absolute-page flex-1 p-3"
      >
        <Column>
          <Row>
            <Text className="color-blue-gray" size={14}>
              Cliente <b>{order?.company?.companyName}</b>
            </Text>
          </Row>
          <Row>
            <Text className="color-blue-gray" size={14}>
              Pedido <b>{order?.protheusOrderSale}</b>
            </Text>
          </Row>
          <Row>
            <Text className="color-blue-gray" size={14}>
              Nota Fiscal <b>{`${order?.invoiceNumber}/${order?.invoiceSerialNumber}`}</b>
            </Text>
          </Row>
        </Column>

        <Column>
          <Text className="mt-4 mb-2 color-blue-gray font-bold" size={16}>
            Instruções para solicitar a correção da nota:
          </Text>

          <ul className="list-instructions">
            <li>Usar apenas letras maiúsculas;</li>
            <li>Não utilizar acento ou caracteres especiais;</li>
            <li>Sempre informar natureza da correção (endereço de entrega, correção de volumes, transportadora...);</li>
            <li>Para acrescentar uma informação, e quando não houver endereço de entrega nas informações adicionais, seguir o padrão “CONSIDERAR A SEGUINTE INFORMACAO FALTANTE XXXXX";</li>
            <li>Para alterar uma informação, e quando houver endereço de entrega nas informações adicionais, seguir o padrão “ONDE LE SE XXXXXX ALTERAR PARA XXXXXX”;</li>
          </ul>
        </Column>

        {order?.correctionLetterKey &&
          <Alert
            title="Importante"
            titleFontSize={14}
            description="Essa nota fiscal já teve uma correção aplicada 
          anteriormente. Ao enviar uma nova solicitação, deve-se incluir as 
          correções anteriores."
            descriptionFontSize={14}
            img={important}
          />
        }

        <Column className="mt-5 description">
          <Text className="mb-2 color-blue-gray" size={14}>
            Informe a correção que deve ser aplicada a nota fiscal:
          </Text>

          <TextArea
            stateName="description"
            id="description"
            onChange={(e) => newDescription(e)}
            value={description}
            minLength={MIN_DESCRIPTION_LENGHT}
            maxLength={MAX_DESCRIPTION_LENGHT}
            isTrimWhiteSpace={true}
            name="Descrição"
            placeholder="Digite"
            required
            style={{
              flex: 1,
              height: 200,
              minHeight: 200,
            }}
          />

          {error && (
            <Text size={14} className="color-danger animated fadeIn mt-3">
              {error}
            </Text>
          )}

          <Button
            isRequesting={loading}
            form="new_request_correction_form"
            className="button outline mt-4"
            spinnerColor="color-primary"
            width="200px"
          >
            <Text className="color-primary font-bold px-3">
              Solicitar alteração
            </Text>
          </Button>
        </Column>

        <Column className="mt-5 background-infos">
          <Text className="mb-2 color-blue-gray font-bold" size={16}>
            Informações que não podem ser corrigidas na nota fiscal:
          </Text>

          <ol className="list-infos">
            <li>as variáveis que determinam o valor do imposto tais como: base de cálculo, alíquota, diferença de preço, quantidade, valor da operação ou da prestação;</li>
            <li>a correção de dados cadastrais que implique mudança do remetente ou do destinatário;</li>
            <li>a data de emissão ou de saída;</li>
            <li>campos da NF-e de exportação informados na Declaração Única de Exportação – DU-E;</li>
            <li>a inclusão ou alteração de parcelas de vendas a prazo;</li>
          </ol>

          <Text className="mt-2 color-blue-gray" size={14}>
            Para maiores informações, consulte o site da <a href="http://www.nfe.fazenda.gov.br/portal/consultaRecaptcha.aspx?tipoConsulta=resumo&tipoConteudo=d09fwabTnLk=" target="_blank">Receita Federal</a>.
          </Text>
        </Column>
      </Column>
    </Form>
  );
};

export default NoteCorrectionForm;